<script setup lang="ts">
import axios from 'axios'
import { BASE_URL } from '@/environment/environment'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { useDisplay } from 'vuetify/framework'
import { useTheme } from 'vuetify'
import CreateTaskModal from '@/modules/tasks/components/CreateTaskModal.vue'
import TaskCard from '@/modules/tasks/components/TaskCard.vue'

import CollapsibleText from '@/components/createdUi/CollapsibleText.vue'
import ShortFio from '@/components/createdUi/ShortFio.vue'
import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import DateRangeFilterCard from '@/components/createdUi/DateRangeFilterCard.vue'
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import FileAttach from '@/modules/files/components/FileAttach.vue'
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useTasksStore } from '@/modules/tasks/stores/tasksStore'
import type { IResponsibilityListItem } from '@/modules/tasks/types/IResponsibilityListItem'
import type { TResponsibilitySourceType, TTasksTab } from '@/modules/tasks/types/IResponsibilityListParent'
import { formatDateTime, getFullName } from '@/utils/formatters'
import { openChat } from '@/utils/openChat'
import { useTableHideCompleted } from '@/composables/useTableHideCompleted'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoRows } from '@/modules/onboarding/composables/useOnboardingDemoRows'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { TASKS_ANCHORS, TASKS_CONTEXTS } from '@/modules/onboarding/scenarios/tasks/anchors'
import { TASKS_CARD_DEMO_INDEX } from '@/modules/onboarding/scenarios/tasks/adapter'

defineEmits(['click:prev', 'click:next', 'click:first', 'click:last'])

const theme = useTheme()
const { xs, smAndDown } = useDisplay()
const feedbackStore = useFeedbackStore()
const apiStore = useApiStore()
const tasksStore = useTasksStore()
const route = useRoute()
const router = useRouter()

const formTabValue = ref<TTasksTab>('all')

const isCreateTaskModalOpen = ref(false)

const showDeleteEntryDialog = ref(false)
const entryToDelete = ref<IResponsibilityListItem | null>(null)

const { currentSortString, nextCursor } = storeToRefs(tasksStore)
const { itemsPerPage } = useTableItemsPerPage('tasks', 25)
const { hideCompleted } = useTableHideCompleted('tasks')
tasksStore.filters.is_active = hideCompleted.value ? true : null

tasksStore.itemsPerPage = itemsPerPage.value
watch(itemsPerPage, newVal => {
  tasksStore.itemsPerPage = newVal
})

const filterConfigs: IFilterConfig[] = [
  { key: 'supervisors', labelPrefix: 'Постановщик: ', type: 'tree' },
  { key: 'performers', labelPrefix: 'Исполнитель: ', type: 'tree' },
  { key: 'status', labelPrefix: 'Статус: ', type: 'status', getCustomLabel: (val: string) => getStatusLabel(val) },
  { key: 'type', labelPrefix: 'Тип: ', type: 'tree' },
  { key: 'involvement', labelPrefix: 'Роль: ', type: 'tree' },
  { key: 'source_types', labelPrefix: 'Основание: ', type: 'tree' },
  { key: 'date_time', labelPrefix: 'Дата: ', type: 'date_range', rangeStartKey: 'created_after', rangeEndKey: 'created_before' },
  { key: 'is_active', labelPrefix: '', type: 'boolean', silent: true },
  { key: 'is_overdue', labelPrefix: 'Просрочено: ', type: 'boolean', getCustomLabel: () => 'Просрочено' },
  { key: 'is_high_priority', labelPrefix: 'Важные: ', type: 'boolean', getCustomLabel: () => 'Важные' },
  { key: 'requires_action', labelPrefix: 'Требует действия: ', type: 'boolean', getCustomLabel: () => 'Требует действия' },
]

const tableLogic = useTableFilters({
  filters: computed(() => tasksStore.filters),
  searchChoices: computed(() => tasksStore.searchChoices) as any,
  filterChoices: computed(() => tasksStore.filterChoices) as any,
  filterLoading: tasksStore.filterLoading,
  fetchFilterChoices: (key: string) => tasksStore.fetchFilterChoices(key),
  fetchData: (cursor?: string, limit?: number, throwOnError?: boolean) => tasksStore.fetchEntries(cursor, limit, throwOnError),
  realtimeEnabled: true,
  currentSortString,
  itemsPerPage,
  resetFilters: () => {
    const currentIsActive = tasksStore.filters.is_active
    tasksStore.resetFilters(formTabValue.value === 'all' ? [] : [formTabValue.value])
    tasksStore.filters.is_active = currentIsActive
  },
  nextCursor,
  loadedItemsCount: computed(() => tasksStore.entries.length),
  totalItemsCount: computed(() => tasksStore.totalCount),
  sortMap: {
    deadline_time: 'deadline_time',
    status: 'status',
    supervisor: 'supervisor',
  },
  filterConfigs,
})

const isOverdueFilterActive = computed(() => tasksStore.filters.is_overdue === true)
const isPriorityFilterActive = computed(() => tasksStore.filters.is_high_priority === true)
const isRequiresActionFilterActive = computed(() => tasksStore.filters.requires_action === true)

const displayedActiveFilterChips = computed(() => tableLogic.activeFilterChips.value)

const toggleOverdueFilter = () => {
  tasksStore.filters.is_overdue = isOverdueFilterActive.value ? null : true
  tableLogic.triggerServerFilter()
}

const togglePriorityFilter = () => {
  tasksStore.filters.is_high_priority = isPriorityFilterActive.value ? null : true
  tableLogic.triggerServerFilter()
}

const toggleRequiresActionFilter = () => {
  tasksStore.filters.requires_action = isRequiresActionFilterActive.value ? null : true
  tableLogic.triggerServerFilter()
}

function getStatusLabel(status: string) {
  if (!status) return 'Ожидает'
  const statuses = apiStore.responsibilityEntriesStatuses
  return (statuses as Record<string, string>)[status] || status
}

const parseDateFromInput = (dateString: string): Date | null => {
  if (!dateString) return null
  const [day, month, year] = dateString.split('.').map(Number)
  if (!day || !month || !year) return null
  return new Date(year, month - 1, day)
}

const applyDateFilterWrapper = () => {
  tasksStore.filters.created_after = parseDateFromInput(tableLogic.tempValues.value.created_after || '')
  tasksStore.filters.created_before = parseDateFromInput(tableLogic.tempValues.value.created_before || '')
  tableLogic.triggerServerFilter()
  tableLogic.filterMenus.value.date_time = false
}

const headers = computed(() => [
  { title: 'Задача', key: 'task', sortable: false },
  { title: 'Подзадачи', key: 'subtasks', sortable: false },
  { title: 'Статус', key: 'status', sortable: true, filterType: 'tree', filterKey: 'status' },
  { title: 'Срок', key: 'deadline_time', sortable: true, filterType: 'date_range', filterKey: 'date_time' },
  { title: 'Постановщик', key: 'supervisor', sortable: true, filterType: 'tree', filterKey: 'supervisors' },
  { title: 'Исполнитель', key: 'responsible_employee', sortable: false, filterType: 'tree', filterKey: 'performers' },
  { title: 'Основание', key: 'source_str', sortable: false },
] as any[])

const displayedItems = computed(() => {
  const start = (tableLogic.currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return tasksStore.entries.slice(start, end)
})

/* Онбординг: демо-задачи первыми в таблице и действия, которыми сценарий раскрывает фильтры и открывает карточку */
const onboardingStore = useOnboardingStore()
const { rows: tableRows } = useOnboardingDemoRows('responsibility_entry', displayedItems)
const onboardingCardDemoId = computed(() => onboardingStore.activeDemoItems[TASKS_CARD_DEMO_INDEX]?.list.id ?? null)
const isFiltersPanelOpen = ref<number | undefined>(undefined)

useOnboardingContext({
  names: [TASKS_CONTEXTS.table],
  scenarioId: 'tasks',
  root: () => document.body,
  actions: {
    openFiltersPanel: () => {
      if (smAndDown.value)
        isFiltersPanelOpen.value = 0
    },
    openDemoCard: () => { feedbackStore.globalResponsibilityEntryId = onboardingCardDemoId.value },
    closeCard: () => { feedbackStore.globalResponsibilityEntryId = null },
  },
})

const isCompleteDialogOpen = ref(false)
const completeReportText = ref('')
const completeFiles = ref<File[]>([])
const completeFileAttachRef = ref<InstanceType<typeof FileAttach> | null>(null)
const taskToComplete = ref<IResponsibilityListItem | null>(null)

// Координаторскую задачу без постановщика создаёт бэк автоматически (вручную is_coordinator_entry не ставится)
const isAutoCreatedEntry = (item: IResponsibilityListItem) => Boolean(item.is_coordinator_entry && !item.supervisor?.id)

const completeModalSupervisor = computed(() => {
  if (!taskToComplete.value?.supervisor) return '-'
  return getFullName(taskToComplete.value.supervisor) || '-'
})

const completeModalTitle = computed(() => {
  return taskToComplete.value?.task || taskToComplete.value?.title || '-'
})

const completeModalDeadline = computed(() => {
  if (!taskToComplete.value?.deadline_time) return '-'
  return formatDateTime(taskToComplete.value.deadline_time) || '-'
})

const expandedTaskIds = ref<Set<number>>(new Set())
const loadingSubtasksTaskIds = ref<Set<number>>(new Set())

function isSubtasksExpanded(id?: number): boolean {
  return id ? expandedTaskIds.value.has(id) : false
}

function isSubtasksLoading(id?: number): boolean {
  return id ? loadingSubtasksTaskIds.value.has(id) : false
}

async function toggleSubtasksExpand(item: IResponsibilityListItem) {
  if (!item.id) return
  if (loadingSubtasksTaskIds.value.has(item.id)) return

  if (expandedTaskIds.value.has(item.id)) {
    expandedTaskIds.value.delete(item.id)
    expandedTaskIds.value = new Set(expandedTaskIds.value)
    return
  }

  if (!item.subtasks || item.subtasks.length === 0) {
    loadingSubtasksTaskIds.value.add(item.id)
    loadingSubtasksTaskIds.value = new Set(loadingSubtasksTaskIds.value)
    try {
      const res = await axios.get(`${BASE_URL}/api/responsibility/responsibility-entries/${item.id}/subtasks/`)
      item.subtasks = res.data?.results ?? res.data ?? []
    } catch (e) {
      console.error('Ошибка загрузки подзадач:', e)
    } finally {
      loadingSubtasksTaskIds.value.delete(item.id)
      loadingSubtasksTaskIds.value = new Set(loadingSubtasksTaskIds.value)
    }
  }

  expandedTaskIds.value.add(item.id)
  expandedTaskIds.value = new Set(expandedTaskIds.value)
}

function getCompletedSubtasksCount(item: IResponsibilityListItem): number {
  if (typeof item.completed_subtasks_count === 'number') {
    return item.completed_subtasks_count
  }
  if (Array.isArray(item.subtasks)) {
    return item.subtasks.filter(s => s.status === 'completed').length
  }
  return 0
}

function getSubtasksTotalCount(item: IResponsibilityListItem): number {
  if (typeof item.subtasks_count === 'number') return item.subtasks_count
  if (Array.isArray(item.subtasks)) return item.subtasks.length
  return 0
}

function getStatusColor(status?: string): string {
  switch (status) {
    case 'completed':
      return 'success'
    case 'in_progress':
      return 'warning'
    case 'rejected':
    case 'cancelled':
      return 'error'
    case 'new':
    default:
      return 'info'
  }
}

function getSourceTypeLabel(sourceType?: TResponsibilitySourceType): string {
  switch (sourceType) {
    case 'adverseevent':
      return 'Нежелательное событие'
    case 'ticket':
      return 'Заявка'
    case 'auditorreport':
      return 'Аудит'
    case 'improvementsuggestion':
      return 'Предложение по улучшению'
    case 'document':
      return 'Документ'
    case 'appeal':
      return 'Обращение граждан'
    case 'responsibilityentry':
      return 'Поручение'
    default:
      return ''
  }
}

function formatTaskDeadline(dateStr: string | null | undefined, isOverdue?: boolean) {
  if (!dateStr) return { line1: '', line2: '', isOverdue: false }
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return { line1: '', line2: '', isOverdue: false }

  const now = new Date()

  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const timeStr = `${hours}:${minutes}`

  if (isOverdue) {
    const diffMs = now.getTime() - d.getTime()
    const diffDays = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)))
    const daysLabel = diffDays === 1 ? '1 день' : diffDays < 5 ? `${diffDays} дня` : `${diffDays} дней`
    return {
      line1: 'Просрочена:',
      line2: daysLabel,
      isOverdue: true,
    }
  }

  const isToday = d.toDateString() === now.toDateString()
  const tomorrow = new Date(now)
  tomorrow.setDate(now.getDate() + 1)
  const isTomorrow = d.toDateString() === tomorrow.toDateString()

  if (isToday) {
    return { line1: 'Сегодня', line2: timeStr, isOverdue: false }
  }
  if (isTomorrow) {
    return { line1: 'Завтра', line2: timeStr, isOverdue: false }
  }

  const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
  const day = d.getDate()
  const month = months[d.getMonth()]
  return { line1: `${day} ${month}`, line2: timeStr, isOverdue: false }
}

const roleOptions = computed(() => {
  const backendChoices = tasksStore.filterChoices['involvement'] || []
  return backendChoices.map((c: any) => {
    const baseLabel = c.label || c.name || String(c.id)
    const countPart = c.count != null ? ` (${c.count})` : ''
    return {
      value: String(c.id),
      label: `${baseLabel}${countPart}`,
    }
  })
})

const sourceTypeOptions = computed(() => {
  const backendChoices = tasksStore.filterChoices['source_types'] || []
  return backendChoices.map((c: any) => {
    let baseLabel = c.label || c.name || String(c.id)
    if (c.id === 'improvementsuggestion' || baseLabel.toLowerCase().includes('предложени')) {
      baseLabel = 'ППУ'
    }
    const countPart = c.count != null ? ` (${c.count})` : ''
    return {
      value: String(c.id),
      label: `${baseLabel}${countPart}`,
    }
  })
})

const selectedRolesDisplayText = computed(() => {
  const selected = tasksStore.filters.involvement || []
  if (selected.length === 0) return ''
  if (selected.length === 1) {
    const opt = roleOptions.value.find(o => o.value === selected[0])
    return opt ? opt.label : selected[0]
  }
  return `Выбрано (${selected.length})`
})

const selectedRolesModel = computed({
  get: () => tasksStore.filters.involvement || [],
  set: (vals: string[]) => {
    tasksStore.filters.involvement = vals
    tableLogic.triggerServerFilter()
  },
})

const selectedSourcesDisplayText = computed(() => {
  const selected = tasksStore.filters.source_types || []
  if (selected.length === 0) return ''
  if (selected.length === 1) {
    const opt = sourceTypeOptions.value.find(o => o.value === selected[0])
    return opt ? opt.label : selected[0]
  }
  return `Выбрано (${selected.length})`
})

const selectedSourceTypesModel = computed({
  get: () => (tasksStore.filters.source_types || []) as string[],
  set: (vals: string[]) => {
    tasksStore.filters.source_types = vals as TResponsibilitySourceType[]
    tableLogic.triggerServerFilter()
  },
})

const handleTogglePin = async (item: IResponsibilityListItem) => {
  if (!item.id) return
  const prevPinned = Boolean(item.is_pinned)
  const newPinned = await tasksStore.togglePin(item.id, prevPinned)
  if (newPinned !== prevPinned) {
    await tasksStore.fetchEntries()
  }
}

const handleTogglePriority = async (item: IResponsibilityListItem) => {
  await tasksStore.togglePriority(item)
}

const showIncompleteSubtasksDialog = ref(false)
const taskPendingIncompleteConfirm = ref<IResponsibilityListItem | null>(null)

const hasIncompleteSubtasks = (item: IResponsibilityListItem) =>
  (item.subtasks_count ?? 0) > (item.completed_subtasks_count ?? 0)

/*
 * Завершение из бургера: при незавершённых подзадачах — сначала подтверждение,
 * затем окно отчёта, если он обязателен (как в TaskCard), иначе завершаем сразу.
 */
function handleCompleteTask(item: IResponsibilityListItem) {
  if (hasIncompleteSubtasks(item)) {
    taskPendingIncompleteConfirm.value = item
    showIncompleteSubtasksDialog.value = true
    return
  }
  void proceedCompleteTask(item)
}

function handleIncompleteSubtasksConfirm(confirmed: boolean) {
  const item = taskPendingIncompleteConfirm.value

  showIncompleteSubtasksDialog.value = false
  taskPendingIncompleteConfirm.value = null
  if (confirmed && item)
    void proceedCompleteTask(item)
}

async function proceedCompleteTask(item: IResponsibilityListItem) {
  if (item.is_report_required !== false) {
    taskToComplete.value = item
    completeReportText.value = ''
    completeFiles.value = []
    isCompleteDialogOpen.value = true
    return
  }

  if (!item.id) return
  await tasksStore.completeTask(item.id, '')
  await tasksStore.fetchEntries()
}

async function submitCompleteTask() {
  if (!taskToComplete.value?.id) return
  await tasksStore.completeTask(
    taskToComplete.value.id,
    completeReportText.value.trim(),
    completeFiles.value.length > 0 ? completeFiles.value : undefined,
  )
  isCompleteDialogOpen.value = false
  completeReportText.value = ''
  completeFiles.value = []
  await tasksStore.fetchEntries()
}

const canEditTaskRow = (item: IResponsibilityListItem) => {
  return Boolean(item.actions?.includes('manage')) && !item.is_coordinator_entry
}

// Срочность меняется PATCH-ем задачи — бэк пускает только с manage
const canTogglePriorityRow = (item: IResponsibilityListItem) => Boolean(item.actions?.includes('manage'))

const canCompleteRow = (item: IResponsibilityListItem) => Boolean(item.actions?.includes('complete'))

// Как в TaskCard: без явного false с бэка отчёт считается обязательным
const isCompleteReportRequired = computed(() => taskToComplete.value?.is_report_required !== false)

const getReportText = (item: IResponsibilityListItem) => {
  if (item.last_report) return item.last_report
  if (item.report) return item.report
  if (Array.isArray(item.reports) && item.reports.length > 0) {
    return item.reports[item.reports.length - 1]?.report || ''
  }
  return ''
}

const getEntrySourceType = (item: IResponsibilityListItem): TResponsibilitySourceType => {
  if (item.source_type) return item.source_type
  if (formTabValue.value !== 'all') return formTabValue.value
  return 'adverseevent'
}

const handleChat = async (item: IResponsibilityListItem) => {
  const employeeId = item.responsible_employee?.id
  if (!employeeId) return
  await openChat(employeeId)
}

const isHideCompleted = computed({
  get: () => tasksStore.filters.is_active === true,
  set: (val: boolean) => {
    tasksStore.filters.is_active = val ? true : null
    hideCompleted.value = val
    tableLogic.triggerServerFilter()
  },
})

const openCreateTask = () => {
  isCreateTaskModalOpen.value = true
}

const togglePinEntry = async (item: IResponsibilityListItem) => {
  await handleTogglePin(item)
}

const copyEntryLink = (item: IResponsibilityListItem) => {
  if (!item.id) return
  const url = `${window.location.origin}/task?task_id=${item.id}`
  navigator.clipboard.writeText(url)
  feedbackStore.showToast({
    type: 'success',
    message: 'Ссылка на задачу скопирована',
  })
}

const confirmDeleteResponsibilityEntry = (item: IResponsibilityListItem) => {
  if (!item?.id) return
  entryToDelete.value = item
  showDeleteEntryDialog.value = true
}

const handleDeleteResponsibilityEntry = async (confirmed: boolean) => {
  showDeleteEntryDialog.value = false
  if (!confirmed) {
    entryToDelete.value = null
    return
  }

  const item = entryToDelete.value
  entryToDelete.value = null
  if (!item) return

  const deleted = await tasksStore.deleteEntry(item)
  if (deleted)
    await tasksStore.fetchEntries()
}

const openTaskParent = (sourceType: TResponsibilitySourceType, parentId: number, extraId?: number | null | undefined) => {
  if (sourceType === 'responsibilityentry' || sourceType === 'document')
    return

  if (sourceType === 'auditorreport') {
    feedbackStore.globalAuditNonconformityReportId = parentId
    feedbackStore.globalAuditNonconformityAuditId = extraId ?? null
    return
  }

  feedbackStore.globalFormTab = 2

  if (sourceType === 'adverseevent') {
    feedbackStore.globalAdverseEventId = parentId
    feedbackStore.createOrUpdateAdverseEvent = true
    return
  }

  if (sourceType === 'ticket') {
    feedbackStore.globalTicketId = parentId
    return
  }

  if (sourceType === 'improvementsuggestion') {
    feedbackStore.globalImprovementId = parentId
    return
  }

  if (sourceType === 'appeal') {
    feedbackStore.globalCitizenInquiryId = String(parentId)
  }
}

const handleRowClick = (item: IResponsibilityListItem) => {
  const sourceType = item.source_type
  const props = item.url_props

  if (item.parent_entry || item.is_coordinator_entry === false) {
    feedbackStore.globalResponsibilityEntryId = item.id ?? null
    return
  }

  if (sourceType === 'equipmentwork' && props?.equipmentId) {
    router.push({ path: `/equipment/${props.equipmentId}`, query: { tab: 'works' } })
    return
  }

  if (sourceType === 'signaturecase' && props?.signatureCaseId) {
    router.push({ name: 'ELECTRONIC_SIGNATURE_CASE', params: { id: props.signatureCaseId } })
    return
  }

  if (sourceType === 'adverseevent' && props?.eventId) {
    openTaskParent('adverseevent', props.eventId)
    return
  }

  if (sourceType === 'ticket' && props?.ticketId) {
    openTaskParent('ticket', props.ticketId)
    return
  }

  if (sourceType === 'improvementsuggestion' && props?.suggestionId) {
    openTaskParent('improvementsuggestion', props.suggestionId)
    return
  }

  if (sourceType === 'auditorreport' && props?.reportId) {
    openTaskParent('auditorreport', props.reportId, props.auditId)
    return
  }

  if (sourceType === 'appeal' && props?.appealId) {
    feedbackStore.globalFormTab = 2
    feedbackStore.globalCitizenInquiryId = String(props.appealId)
    return
  }

  if (props?.entryId || item.id) {
    feedbackStore.globalResponsibilityEntryId = props?.entryId ?? item.id ?? null
  }
}

function getQueryValue(value: unknown): string | undefined {
  if (Array.isArray(value)) return typeof value[0] === 'string' ? value[0] : undefined
  return typeof value === 'string' ? value : undefined
}

function isTaskSourceType(value: string | undefined): value is TResponsibilitySourceType {
  return value === 'ticket'
    || value === 'adverseevent'
    || value === 'auditorreport'
    || value === 'improvementsuggestion'
}

async function openLinkedTaskFromRoute() {
  const taskId = Number(getQueryValue(route.query.task_id))
  const sourceType = getQueryValue(route.query.task_source)
  const parentId = Number(getQueryValue(route.query.task_parent))

  if (!taskId && !parentId) return

  const task = taskId
    ? tasksStore.entries.find(item => item.id === taskId)
    : undefined

  if (task) {
    handleRowClick(task)
  } else if (sourceType === 'responsibilityentry' && taskId) {
    feedbackStore.globalResponsibilityEntryId = taskId
  } else if (parentId > 0 && isTaskSourceType(sourceType)) {
    openTaskParent(sourceType, parentId)
  }

  const query = { ...route.query }
  delete query.task_id
  delete query.task_source
  delete query.task_parent
  await router.replace({ query })
}

function isTaskOverdue(item: IResponsibilityListItem): boolean {
  if (!item?.deadline_time || item.status === 'completed') {
    return false
  }
  const deadline = new Date(item.deadline_time)
  if (Number.isNaN(deadline.getTime())) {
    return false
  }
  return deadline < new Date()
}

const rowProps = (row: { item: IResponsibilityListItem }) => ({
  onClick: () => handleRowClick(row.item),
  class: {
    'row--requires-action': row.item.requires_action === true,
  },
})

/** Смена вкладки: сброс фильтров под source_type (или без него на «Все») и перезагрузка */
const loadForTab = async (tab: TTasksTab, isInitial = false) => {
  formTabValue.value = tab
  if (!isInitial) {
    const currentIsActive = tasksStore.filters.is_active
    tasksStore.resetFilters(tab === 'all' ? [] : [tab])
    tasksStore.filters.is_active = currentIsActive
  } else if (tab !== 'all') {
    tasksStore.setSourceType(tab)
  }
  tasksStore.currentSortString = null
  tableLogic.currentPage.value = 1
  await tasksStore.fetchEntries()
}

onMounted(async () => {
  tasksStore.fetchFilterChoices('involvement')
  tasksStore.fetchFilterChoices('source_types')
  await loadForTab(formTabValue.value, true)
  await openLinkedTaskFromRoute()

  // Автостарт только если по ссылке не открыта конкретная задача
  if (feedbackStore.globalResponsibilityEntryId === null)
    void onboardingStore.autoStart('tasks')
})
</script>

<template>
  <div class="d-flex flex-column">
    <!-- Кнопка «+ Создать задачу» и поиск -->
    <div
      class="d-flex ga-4 mb-4"
      :class="smAndDown ? 'flex-column align-stretch w-100' : 'align-center'"
    >
      <VBtn
        color="primary"
        prepend-icon="ri-add-line"
        class="text-none font-weight-medium rounded-lg px-5 flex-shrink-0"
        :class="{ 'w-100': smAndDown }"
        :block="smAndDown"
        :loading="feedbackStore.isGlobalLoading"
        :data-onboarding="TASKS_ANCHORS.table.create"
        @click="openCreateTask"
      >
        Создать задачу
      </VBtn>

      <div
        class="d-flex align-center search-input-box"
        :class="{ 'w-100 search-input-box--mobile': smAndDown }"
        :data-onboarding="TASKS_ANCHORS.table.search"
      >
        <VIcon icon="ri-search-line" size="20" class="search-icon text-medium-emphasis me-2 flex-shrink-0" />
        <input
          v-model="tableLogic.searchFilter.value"
          placeholder="Найти...."
          class="search-input"
          @keyup.enter="tableLogic.applySearchFilter()"
        />
      </div>
      <OnboardingLaunchButton
        scenario="tasks"
        :class="smAndDown ? 'align-self-end' : 'ms-auto'"
      />
    </div>

    <div class="wrapper">
      <!-- Панель фильтров над таблицей внутри карточки таблицы -->
      <div class="pa-4 pb-2">
        <!-- Мобильная версия (smAndDown): кнопки-фильтры в колонку под Expansion Panels -->
        <div v-if="smAndDown" class="d-flex flex-column ga-3 w-100">
          <div class="d-flex align-center justify-space-between w-100">
            <VSwitch
              :data-onboarding="TASKS_ANCHORS.table.hideCompleted"
              v-model="isHideCompleted"
              label="Скрыть завершенные"
              density="compact"
              color="primary"
              hide-details
            />
          </div>

          <VExpansionPanels v-model="isFiltersPanelOpen" class="w-100">
            <VExpansionPanel>
              <VExpansionPanelTitle class="d-flex align-center py-2 px-4">
                <div class="d-flex align-center ga-2">
                  <VIcon icon="ri-filter-3-line" size="20" />
                  <span class="text-subtitle-1 font-weight-medium">Фильтры</span>
                </div>
              </VExpansionPanelTitle>
              <VExpansionPanelText>
                <div class="d-flex flex-column ga-3 w-100 pt-2">
                  <VSelect
                    v-model="selectedRolesModel"
                    :items="roleOptions"
                    item-title="label"
                    item-value="value"
                    label="Роль"
                    :data-onboarding="TASKS_ANCHORS.table.role"
                    variant="outlined"
                    density="compact"
                    multiple
                    hide-details
                    class="w-100"
                  >
                    <template #selection="{ index }">
                      <span v-if="index === 0" class="text-no-wrap">{{ selectedRolesDisplayText }}</span>
                    </template>
                  </VSelect>

                  <VSelect
                    v-model="selectedSourceTypesModel"
                    :items="sourceTypeOptions"
                    item-title="label"
                    item-value="value"
                    label="Основание"
                    :data-onboarding="TASKS_ANCHORS.table.source"
                    variant="outlined"
                    density="compact"
                    multiple
                    hide-details
                    class="w-100"
                  >
                    <template #selection="{ index }">
                      <span v-if="index === 0" class="text-no-wrap">{{ selectedSourcesDisplayText }}</span>
                    </template>
                  </VSelect>

                  <VBtn
                    variant="outlined"
                    height="40"
                    class="text-none font-weight-medium filter-btn-overdue w-100"
                    :data-onboarding="TASKS_ANCHORS.table.quickFirst"
                    :class="{ 'filter-btn-overdue--active': isOverdueFilterActive }"
                    :loading="feedbackStore.isGlobalLoading"
                    @click="toggleOverdueFilter"
                  >
                    Просрочены
                    <VIcon end icon="ri-alert-line" size="16" />
                  </VBtn>

                  <VBtn
                    variant="outlined"
                    height="40"
                    class="text-none font-weight-medium filter-btn-priority w-100"
                    :class="{ 'filter-btn-priority--active': isPriorityFilterActive }"
                    :loading="feedbackStore.isGlobalLoading"
                    @click="togglePriorityFilter"
                  >
                    Важные
                    <VIcon end icon="ri-fire-line" size="16" />
                  </VBtn>

                  <VBtn
                    :prepend-icon="tableLogic.getFilterIcon('requires_action')"
                    variant="outlined"
                    color="secondary"
                    height="40"
                    class="text-none font-weight-medium filter-btn-action w-100"
                    :data-onboarding="TASKS_ANCHORS.table.quickLast"
                    :class="{ 'filter-btn-action--active': isRequiresActionFilterActive }"
                    :loading="feedbackStore.isGlobalLoading"
                    @click="toggleRequiresActionFilter"
                  >
                    Требует действия
                  </VBtn>

                  <VBtn
                    v-if="tableLogic.hasActiveFilters.value"
                    variant="outlined"
                    color="secondary"
                    height="40"
                    prepend-icon="ri-delete-bin-line"
                    class="text-none font-weight-medium text-color-secondary w-100"
                    :data-onboarding="TASKS_ANCHORS.table.resetFilters"
                    :loading="feedbackStore.isGlobalLoading"
                    @click="tableLogic.renewDataAndResetFilters()"
                  >
                    Сбросить фильтры
                  </VBtn>
                </div>
              </VExpansionPanelText>
            </VExpansionPanel>
          </VExpansionPanels>
        </div>

        <!-- Десктопная версия (smAndUp) -->
        <div v-else class="d-flex align-center justify-space-between flex-wrap ga-3 w-100">
          <div class="d-flex align-center flex-wrap ga-3">
            <div class="filter-select-auto">
              <VSelect
                v-model="selectedRolesModel"
                :items="roleOptions"
                :menu-props="{ width: 'auto', minWidth: '100%' }"
                item-title="label"
                item-value="value"
                label="Роль"
                :data-onboarding="TASKS_ANCHORS.table.role"
                variant="outlined"
                density="compact"
                multiple
                hide-details
              >
                <template #selection="{ index }">
                  <span v-if="index === 0" class="text-no-wrap">{{ selectedRolesDisplayText }}</span>
                </template>
              </VSelect>
            </div>

            <div class="filter-select-auto">
              <VSelect
                v-model="selectedSourceTypesModel"
                :items="sourceTypeOptions"
                :menu-props="{ width: 'auto', minWidth: '100%' }"
                item-title="label"
                item-value="value"
                label="Основание"
                :data-onboarding="TASKS_ANCHORS.table.source"
                variant="outlined"
                density="compact"
                multiple
                hide-details
              >
                <template #selection="{ index }">
                  <span v-if="index === 0" class="text-no-wrap">{{ selectedSourcesDisplayText }}</span>
                </template>
              </VSelect>
            </div>

            <VBtn
              variant="outlined"
              height="40"
              class="text-none font-weight-medium filter-btn-overdue"
              :data-onboarding="TASKS_ANCHORS.table.quickFirst"
              :class="{ 'filter-btn-overdue--active': isOverdueFilterActive }"
              :loading="feedbackStore.isGlobalLoading"
              @click="toggleOverdueFilter"
            >
              Просрочены
              <VIcon end icon="ri-alert-line" size="16" />
            </VBtn>

            <VBtn
              variant="outlined"
              height="40"
              class="text-none font-weight-medium filter-btn-priority"
              :class="{ 'filter-btn-priority--active': isPriorityFilterActive }"
              :loading="feedbackStore.isGlobalLoading"
              @click="togglePriorityFilter"
            >
              Важные
              <VIcon end icon="ri-fire-line" size="16" />
            </VBtn>

            <VBtn
              :prepend-icon="tableLogic.getFilterIcon('requires_action')"
              variant="outlined"
              color="secondary"
              height="40"
              class="text-none font-weight-medium filter-btn-action"
              :data-onboarding="TASKS_ANCHORS.table.quickLast"
              :class="{ 'filter-btn-action--active': isRequiresActionFilterActive }"
              :loading="feedbackStore.isGlobalLoading"
              @click="toggleRequiresActionFilter"
            >
              Требует действия
            </VBtn>

            <VBtn
              v-if="tableLogic.hasActiveFilters.value"
              variant="outlined"
              color="secondary"
              height="40"
              prepend-icon="ri-delete-bin-line"
              class="text-none font-weight-medium text-color-secondary"
              :data-onboarding="TASKS_ANCHORS.table.resetFilters"
              :loading="feedbackStore.isGlobalLoading"
              @click="tableLogic.renewDataAndResetFilters()"
            >
              Сбросить фильтры
            </VBtn>
          </div>

          <VSwitch
            :data-onboarding="TASKS_ANCHORS.table.hideCompleted"
            v-model="isHideCompleted"
            label="Скрыть завершенные"
            density="compact"
            color="primary"
            hide-details
          />
        </div>
      </div>

      <!-- Активные чипы фильтров (если есть) -->
      <div
        v-if="displayedActiveFilterChips.length"
        class="filter-chip d-flex align-center gap-2 px-4 pb-2 flex-wrap"
      >
        <VChip
          v-for="chip in displayedActiveFilterChips"
          :key="chip.key || chip.id || `${chip.config?.key || chip.type}-${chip.value ?? 'no-value'}`"
          closable
          size="small"
          @click:close="tableLogic.removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>

      <!-- Таблица задач (Скриншот 1 и 2) -->
      <VDataTableServer
        :items="tableRows"
        :items-length="tasksStore.totalCount"
        :headers="headers as any"
        :items-per-page="itemsPerPage"
        :loading="feedbackStore.isGlobalLoading"
        loading-text=""
        class="no-row-gaps cursor-pointer-table custom-data-table custom-data-table-footer"
        item-value="id"
        :class="[{ 'dark-theme': theme.global.current.value.dark }]"
        @update:options="tableLogic.handlePageChange"
      >
        <template #no-data>
          <div class="text-center text-italic py-4">
            Данные отсутствуют
          </div>
        </template>

        <template #[`header.task`]="{ column }">
          <div class="header-content">
            <span>{{ column.title }}</span>
          </div>
        </template>

        <template #[`header.subtasks`]="{ column }">
          <div class="header-content">
            <span>{{ column.title }}</span>
          </div>
        </template>

        <template #[`header.source_str`]="{ column }">
          <div class="header-content">
            <span>{{ column.title }}</span>
          </div>
        </template>

        <!-- Кастомные заголовки колонок с сортировкой/фильтрацией -->
        <template
          v-for="col in headers.filter(h => h.filterType)"
          :key="col.key"
          #[`header.${col.key}`]="{ column }"
        >
          <div
            class="header-content d-flex align-center"
            :data-onboarding="TASKS_ANCHORS.table.filterPrefix + col.key"
          >
            <span>{{ column.title }}</span>

            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="tableLogic.getSortIcon(col.key)"
              :class="{
                'is-active': tableLogic.isSortActive(col.key),
                'is-desc': tableLogic.isSortDesc(col.key),
              }"
            />

            <div
              v-if="tableLogic.filterLoading?.[col.filterKey!]"
              class="d-flex align-center justify-center ml-1"
              style="width: 28px; height: 28px"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>

            <VIconBtn
              v-else
              variant="plain"
              size="small"
              class="filter-icon-btn"
              :icon="tableLogic.getFilterIcon(col.filterKey!)"
              @click.stop="tableLogic.handleFilterClick(col.filterKey!)"
            />

            <VMenu
              v-if="col.filterType === 'date_range'"
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
              :max-width="220"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <DateRangeFilterCard
                :date-after="tableLogic.tempValues.value.created_after || ''"
                :date-before="tableLogic.tempValues.value.created_before || ''"
                @update:date-after="tableLogic.tempValues.value.created_after = $event"
                @update:date-before="tableLogic.tempValues.value.created_before = $event"
                @apply="applyDateFilterWrapper"
              />
            </VMenu>

            <VMenu
              v-else
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom start"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <UniversalFilterTree
                :field-key="col.filterKey!"
                :store="tasksStore"
                :model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] || []"
                :placeholder="`Поиск: ${column.title}`"
                @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
                @apply="tableLogic.applyFilter(col.filterKey!)"
              />
            </VMenu>
          </div>
        </template>

        <!-- Строки таблицы (Скриншот 1 и 2) - ровно 7 колонок! -->
        <template #item="{ item }">
          <!-- 1. Родительская задача -->
          <tr
            :key="item.id"
            class="v-data-table__tr cursor-pointer task-parent-row"
            :class="{
              'row--requires-action': item.requires_action === true,
              'task-row--pinned': item.is_pinned,
            }"
            :data-onboarding="item.id === onboardingCardDemoId ? TASKS_ANCHORS.table.demoRow : undefined"
            @click="handleRowClick(item)"
          >
            <!-- 1. Задача: бургер слева + огонек + название задачи (Скриншот 1 и 2) -->
            <td class="v-data-table__td ps-3 pe-2">
              <div class="d-flex align-center ga-2">
                <VMenu location="bottom start" content-class="entity-form-menu">
                  <template #activator="{ props: menuProps }">
                    <VBtn
                      v-bind="menuProps"
                      :data-onboarding="item.id === onboardingCardDemoId ? TASKS_ANCHORS.table.demoRowMenu : undefined"
                      variant="text"
                      icon
                      density="compact"
                      size="small"
                      color="secondary"
                      class="flex-shrink-0"
                      @click.stop
                    >
                      <VIcon icon="ri-menu-line" size="18" />
                    </VBtn>
                  </template>

                  <VList density="compact" class="py-1 task-burger-menu">
                    <!-- Отметить как срочную -->
                    <VListItem
                      v-if="canTogglePriorityRow(item)"
                      class="cursor-pointer task-burger-menu-item"
                      @click="handleTogglePriority(item)"
                    >
                      <div class="d-flex align-center ga-2">
                        <VIcon icon="ri-fire-line" size="20" class="flex-shrink-0" />
                        <span class="task-burger-menu-text">
                          {{ item.is_high_priority ? 'Снять отметку срочной' : 'Отметить как срочную' }}
                        </span>
                      </div>
                    </VListItem>

                    <!-- Закрепить / Открепить -->
                    <VListItem class="cursor-pointer task-burger-menu-item" @click="handleTogglePin(item)">
                      <div class="d-flex align-center ga-2">
                        <VIcon :icon="item.is_pinned ? 'ri-pushpin-fill' : 'ri-pushpin-line'" size="20" class="flex-shrink-0" />
                        <span class="task-burger-menu-text">
                          {{ item.is_pinned ? 'Открепить' : 'Закрепить' }}
                        </span>
                      </div>
                    </VListItem>

                    <!-- Копировать ссылку на задачу -->
                    <VListItem class="cursor-pointer task-burger-menu-item" @click="copyEntryLink(item)">
                      <div class="d-flex align-center ga-2">
                        <VIcon icon="ri-file-copy-line" size="20" class="flex-shrink-0" />
                        <span class="task-burger-menu-text">Копировать ссылку на задачу</span>
                      </div>
                    </VListItem>

                    <!-- Завершить -->
                    <VListItem
                      v-if="canCompleteRow(item)"
                      class="cursor-pointer task-burger-menu-item"
                      @click="handleCompleteTask(item)"
                    >
                      <div class="d-flex align-center ga-2">
                        <VIcon icon="ri-checkbox-circle-line" size="20" class="flex-shrink-0" />
                        <span class="task-burger-menu-text">Завершить</span>
                      </div>
                    </VListItem>

                    <!-- Удалить -->
                    <VListItem
                      v-if="canEditTaskRow(item)"
                      class="cursor-pointer task-burger-menu-item"
                      @click="confirmDeleteResponsibilityEntry(item)"
                    >
                      <div class="d-flex align-center ga-2">
                        <VIcon icon="ri-delete-bin-line" size="20" class="flex-shrink-0" />
                        <span class="task-burger-menu-text">Удалить</span>
                      </div>
                    </VListItem>
                  </VList>
                </VMenu>

                <VIcon
                  v-if="item.is_pinned"
                  icon="ri-pushpin-fill"
                  size="16"
                  color="primary"
                  class="flex-shrink-0"
                />

                <VIcon
                  v-if="item.is_high_priority"
                  icon="ri-fire-line"
                  size="16"
                  color="warning"
                  class="flex-shrink-0"
                />

                <CollapsibleText :text="item.task || item.title || 'Задача'" />
              </div>
            </td>

            <!-- 2. Подзадачи (Свернуто: треугольник вправо ri-arrow-right-s-fill, раскрыто: ri-arrow-down-s-fill) -->
            <td class="v-data-table__td">
              <div
                v-if="getSubtasksTotalCount(item) > 0"
                class="d-inline-flex align-center cursor-pointer text-primary font-weight-medium"
                @click.stop="toggleSubtasksExpand(item)"
              >
                <AnimatedLogoLoader
                  v-if="isSubtasksLoading(item.id)"
                  :size="20"
                />
                <template v-else>
                  <VIcon
                    :icon="isSubtasksExpanded(item.id) ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'"
                    size="18"
                    class="mr-1"
                  />
                  <span>{{ getCompletedSubtasksCount(item) }}/{{ getSubtasksTotalCount(item) }}</span>
                </template>
              </div>
            </td>

            <!-- 3. Статус -->
            <td class="v-data-table__td">
              <VChip
                class="status-badge"
                :class="item.status"
                size="small"
              >
                {{ getStatusLabel(item.status || '') }}
              </VChip>
            </td>

            <!-- 4. Срок -->
            <td class="v-data-table__td">
              <span
                v-if="!item.deadline_time"
                class="task-deadline-empty text-disabled"
              >
                Без срока
              </span>
              <div
                v-else
                class="d-flex flex-column leading-tight"
                :class="{ 'text-error font-weight-medium': formatTaskDeadline(item.deadline_time, item.is_overdue).isOverdue }"
              >
                <span>{{ formatTaskDeadline(item.deadline_time, item.is_overdue).line1 }}</span>
                <span
                  v-if="formatTaskDeadline(item.deadline_time, item.is_overdue).line2"
                  class="text-caption"
                  :class="formatTaskDeadline(item.deadline_time, item.is_overdue).isOverdue ? 'text-error' : 'text-medium-emphasis'"
                >
                  {{ formatTaskDeadline(item.deadline_time, item.is_overdue).line2 }}
                </span>
              </div>
            </td>

            <!-- 5. Постановщик -->
            <td class="v-data-table__td">
              <ShortFio v-if="item.supervisor?.id" :person="item.supervisor" />
              <span v-else-if="isAutoCreatedEntry(item)" class="auto-created-text">Создана автоматически</span>
            </td>

            <!-- 6. Исполнитель -->
            <td class="v-data-table__td">
              <ShortFio v-if="item.responsible_employee?.id" :person="item.responsible_employee" />
            </td>

            <!-- 7. Основание -->
            <td class="v-data-table__td">
              <span>
                {{ (item as any).source_str || getSourceTypeLabel(item.source_type) || '' }}
              </span>
            </td>
          </tr>

          <!-- 2. Раскрытые строки подзадач (ровно 7 колонок!) -->
          <template v-if="isSubtasksExpanded(item.id) && item.subtasks">
            <tr
              v-for="(subtask, sIdx) in item.subtasks"
              :key="`subtask-${subtask.id}`"
              class="v-data-table__tr cursor-pointer bg-surface-variant-subtle subtask-table-row"
              :class="{
                'row--requires-action': subtask.requires_action === true,
              }"
              @click="handleRowClick(subtask)"
            >
              <!-- 1. Задача: название подзадачи с отступом правее текста родителя для наглядной иерархии -->
              <td class="v-data-table__td pe-2" style="padding-left: 64px !important;">
                <CollapsibleText :text="subtask.task || subtask.title || 'Подзадача'" />
              </td>

              <!-- 2. Подзадачи: номер (1, 2...) -->
              <td class="v-data-table__td">
                <span>{{ sIdx + 1 }}</span>
              </td>

              <!-- 3. Статус -->
              <td class="v-data-table__td">
                <VChip
                  class="status-badge"
                  :class="subtask.status"
                  size="small"
                >
                  {{ getStatusLabel(subtask.status || '') }}
                </VChip>
              </td>

              <!-- 4. Срок -->
              <td class="v-data-table__td">
                <span
                  v-if="!subtask.deadline_time"
                  class="task-deadline-empty text-disabled"
                >
                  Без срока
                </span>
                <div
                  v-else
                  class="d-flex flex-column leading-tight"
                  :class="{ 'text-error font-weight-medium': formatTaskDeadline(subtask.deadline_time, subtask.is_overdue).isOverdue }"
                >
                  <span>{{ formatTaskDeadline(subtask.deadline_time, subtask.is_overdue).line1 }}</span>
                  <span
                    v-if="formatTaskDeadline(subtask.deadline_time, subtask.is_overdue).line2"
                    class="text-caption"
                    :class="formatTaskDeadline(subtask.deadline_time, subtask.is_overdue).isOverdue ? 'text-error' : 'text-medium-emphasis'"
                  >
                    {{ formatTaskDeadline(subtask.deadline_time, subtask.is_overdue).line2 }}
                  </span>
                </div>
              </td>

              <!-- 5. Постановщик -->
              <td class="v-data-table__td">
                <ShortFio v-if="subtask.supervisor?.id" :person="subtask.supervisor" />
                <span v-else-if="isAutoCreatedEntry(subtask)" class="auto-created-text">Создана автоматически</span>
              </td>

              <!-- 6. Исполнитель -->
              <td class="v-data-table__td">
                <ShortFio v-if="subtask.responsible_employee?.id" :person="subtask.responsible_employee" />
              </td>

              <!-- 7. Основание: пусто -->
              <td class="v-data-table__td"></td>
            </tr>
          </template>
        </template>

        <template #bottom>
          <div
            class="custom-data-table-footer"
            :class="{ 'hide-next-button': tableLogic.hideNextButton.value }"
          >
            <VDataTableFooter
              show-current-page
              :items-per-page-options="[25, 50, 100]"
              v-bind="$attrs"
              @click:prev="$emit('click:prev', $event)"
              @click:next="$emit('click:next', $event)"
              @click:first="$emit('click:first', $event)"
              @click:last="$emit('click:last', $event)"
            />
          </div>
        </template>
      </VDataTableServer>
    </div>
  </div>

  <!-- Модальное окно создания задачи -->
  <CreateTaskModal
    v-model="isCreateTaskModalOpen"
    @created="tasksStore.fetchEntries()"
  />



  <!-- Диалог подтверждения удаления -->
  <ConfirmDialog
    v-model:is-dialog-visible="showDeleteEntryDialog"
    confirmation-question="Вы действительно хотите удалить эту задачу?"
    confirmation-text="Это необратимый процесс"
    confirm-btn="Удалить"
    reject-btn="Отменить"
    @confirm="handleDeleteResponsibilityEntry"
  />

  <!-- Подтверждение завершения задачи с незавершёнными подзадачами -->
  <ConfirmDialog
    :is-dialog-visible="showIncompleteSubtasksDialog"
    confirmation-question="Завершить задачу?"
    confirmation-text="У задачи есть незавершённые подзадачи. Вы уверены, что хотите завершить задачу?"
    confirm-btn="Завершить"
    reject-btn="Отмена"
    @confirm="handleIncompleteSubtasksConfirm"
    @update:is-dialog-visible="showIncompleteSubtasksDialog = $event"
  />

  <!-- Диалог завершения задачи с отчетом (по макету) -->
  <VDialog
    v-model="isCompleteDialogOpen"
    width="650"
    max-width="650"
    content-class="entity-form-menu"
    :z-index="2500"
  >
    <VCard class="pa-6 rounded-lg">
      <div class="d-flex align-center justify-space-between mb-4">
        <span class="complete-modal-title">
          Отчет по задаче
        </span>
        <CustomCloseButton :close-action="() => isCompleteDialogOpen = false" />
      </div>

      <div class="complete-task-meta mb-5">
        <div>
          Постановщик:
          <span v-if="taskToComplete && isAutoCreatedEntry(taskToComplete)" class="auto-created-text">Создана автоматически</span>
          <template v-else>{{ completeModalSupervisor }}</template>
        </div>
        <div>Задача: {{ completeModalTitle }}</div>
        <div>Крайний срок: {{ completeModalDeadline }}</div>
      </div>

      <div class="d-flex align-start ga-2 mb-3">
        <VTextarea
          v-model="completeReportText"
          label="Отчет"
          placeholder="Опишите выполненные действия"
          persistent-placeholder
          rows="3"
          variant="outlined"
          color="primary"
          class="flex-grow-1"
          hide-details="auto"
          auto-grow
        />
        <VTooltip location="top" text="Прикрепить файлы">
          <template #activator="{ props: tipProps }">
            <VBtn
              v-bind="tipProps"
              icon="ri-attachment-line"
              variant="text"
              size="small"
              color="secondary"
              class="mt-2 flex-shrink-0"
              @click="completeFileAttachRef?.openDialog()"
            />
          </template>
        </VTooltip>
      </div>

      <!-- Прикрепление и отображение файлов через FileAttach -->
      <FileAttach
        ref="completeFileAttachRef"
        v-model:files="completeFiles"
      />

      <div class="d-flex justify-end mt-4">
        <VBtn
          color="primary"
          class="text-none font-weight-medium rounded-lg px-6"
          :block="xs"
          :loading="feedbackStore.isGlobalLoading"
          :disabled="isCompleteReportRequired && !completeReportText.trim()"
          @click="submitCompleteTask"
        >
          Завершить
        </VBtn>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.auto-created-text {
  color: rgba(var(--v-theme-text-primary), var(--v-disabled-opacity));
}

:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
  white-space: nowrap;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

header {
  background: rgb(var(--v-theme-background));
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table tr):hover {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled='true'] {
      display: none !important;
    }
  }
}

.search-input-box {
  width: 100%;
  max-width: 320px;

  &--mobile {
    max-width: 100%;
  }
}

.search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  line-height: 24px;
  width: 100%;
  color: inherit;

  &::placeholder {
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }
}

:deep(.v-data-table__th:nth-child(1)),
:deep(.v-data-table__td:nth-child(1)) {
  min-width: 15rem !important;
  max-width: 20rem !important;
}
:deep(.v-data-table__th:nth-child(2)) { min-width: 7rem !important; }
:deep(.v-data-table__th:nth-child(3)) { min-width: 8rem !important; }
:deep(.v-data-table__th:nth-child(4)) { min-width: 8.5rem !important; }
:deep(.v-data-table__th:nth-child(5)) { min-width: 10rem !important; }
:deep(.v-data-table__th:nth-child(6)),
:deep(.v-data-table__td:nth-child(6)) {
  min-width: 10rem !important;
}
:deep(.v-data-table__th:nth-child(7)) { min-width: 11rem !important; }

.filter-select-auto {
  width: fit-content;
  min-width: 140px;
}

.filter-btn-overdue {
  border-color: rgb(var(--v-theme-error)) !important;
  color: rgb(var(--v-theme-error)) !important;
  border-radius: 8px;

  &--active {
    background-color: rgba(var(--v-theme-error), 0.12) !important;
  }
}

.filter-btn-priority {
  border-color: rgb(var(--v-theme-warning)) !important;
  color: rgb(var(--v-theme-warning)) !important;
  border-radius: 8px;

  &--active {
    background-color: rgba(var(--v-theme-warning), 0.12) !important;
  }
}

.filter-btn-action {
  border-radius: 8px;
  border-color: rgba(var(--v-theme-secondary), 0.5) !important;

  &--active {
    background-color: rgba(var(--v-theme-error), 0.12) !important;
    border-color: rgb(var(--v-theme-error)) !important;
    color: rgb(var(--v-theme-error)) !important;
  }
}

.subtask-table-row {
  background-color: rgba(var(--v-theme-on-surface), 0.02);

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.05) !important;
  }
}

.deadline-cell {
  white-space: nowrap;
}

:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

:deep(.custom-sort-icon) {
  opacity: 0.4;
  transition: transform 0.2s ease, opacity 0.2s ease;

  &.is-active {
    opacity: 1;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

.filter-icon-btn {
  opacity: 0.6;
}


:deep(.custom-data-table tr.row--requires-action > td:first-child),
:deep(.custom-data-table tr.row--requires-action > .v-data-table__td:first-child) {
  border-inline-start: 4px solid rgb(var(--v-theme-error)) !important;
}

:deep(.task-burger-menu) {
  box-shadow: 0px 6px 16px rgba(46, 38, 61, 0.22) !important;
  border-radius: 6px !important;

  .task-burger-menu-item {
    min-height: 36px !important;
    padding: 6px 14px !important;
  }

  .task-burger-menu-text {
    font-family: Inter, sans-serif !important;
    font-size: 15px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    line-height: 20px !important;
    color: rgb(var(--v-theme-on-surface)) !important;
    white-space: nowrap;
  }

  .v-icon {
    color: rgb(var(--v-theme-on-surface)) !important;
  }
}

.complete-modal-title {
  font-family: Inter, sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
  color: rgb(var(--v-theme-text-primary));
}

.complete-task-meta {
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: rgb(var(--v-theme-text-secondary));
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.task-deadline-empty {
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
}
</style>
